import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Button } from "@/components/button";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { Routes } from "@/lib/router";
import { useSelectorModalStore } from "@/lib/store/modal";

/**
 * "Uren opgeslagen — wil je ook iets declareren?"
 *
 * Asked from the modal store rather than from the row that submitted the hours, because that row is
 * gone by the time the question makes sense: submitting hours takes the shift out of the action
 * list, and anything it owned unmounts with it. This outlives that.
 *
 * Only ever raised for a shift where declaring is possible and nothing has been claimed yet — see
 * `use-completed-shift-actions`. Travel costs are the part of a shift an employee is most likely to
 * simply forget, and the moment their hours go in is the last one where the evening is still
 * fresh.
 */
export function ModalDeclarationPrompt() {
  const { t } = useTranslation();
  const history = useHistory();
  const modalRef = useRef<HTMLIonModalElement>(null);
  const modal = useSelectorModalStore.use.declarationPrompt();
  const present = useSelectorModalStore.use.present();
  const dismiss = useSelectorModalStore.use.dismiss();

  const openDeclaration = () => {
    if (!modal.shiftId) {
      return;
    }

    const destination = Routes.ShiftDetail({ id: modal.shiftId }, { search: { declare: true } });

    modalRef.current?.dismiss();
    history.push(destination);
  };

  return (
    <ModalSheetAutoHeight
      ref={modalRef}
      isOpen={modal.isOpen}
      onWillPresent={() => present("declarationPrompt")}
      onWillDismiss={() => dismiss("declarationPrompt")}
    >
      <div className="flex flex-col gap-5 px-6 pb-6 pt-4">
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold leading-[1.4] text-txt-primary">
            {t("modals:declaration-prompt.title")}
          </h2>
          <p className="text-md leading-[1.5] text-txt-secondary">
            {t("modals:declaration-prompt.body")}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <Button expand="block" onClick={openDeclaration}>
            {t("modals:declaration-prompt.confirm")}
          </Button>
          <Button expand="block" fill="clear" onClick={() => modalRef.current?.dismiss()}>
            {t("modals:declaration-prompt.dismiss")}
          </Button>
        </div>
      </div>
    </ModalSheetAutoHeight>
  );
}
